import React, { useMemo, useCallback, useState } from "react";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { getRandomStatusText } from "./utils";
import { getStatusIcon } from "./StatusIcon";
import { StoryCodeActionButtons } from "../StoryCodeActionButtons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ApplyFileEditsBlockProps {
  args: any;
  status: "in-progress" | "succeeded" | "failed" | "aborted";
  toolResult?: any;
  toolCallId?: string;
  messageId: string;
}

export const ApplyFileEditsBlock: React.FC<ApplyFileEditsBlockProps> =
  React.memo(({ args, status, toolResult, toolCallId, messageId }) => {
    const openCodeDiffPanel = useAiContextStore(
      (state) => state.openCodeDiffPanel,
    );
    const setNewLayout = useLayoutStore((store) => store.setNewLayout);

    const [accordionValue, setAccordionValue] = useState<string>("");

    const { hasDiffData, fileEdited, errorMessage, activityId } =
      useMemo(() => {
        const activityId = toolResult?.activityId;
        const fileEdited = args?.filename || toolResult?.filePath || "";
        const hasDiffData = activityId !== undefined && fileEdited;

        const errorMessage =
          (status === "failed" || status === "aborted") &&
          (toolResult?.message ||
            (status === "aborted"
              ? "Tool execution aborted"
              : "Tool execution failed"));

        return { hasDiffData, fileEdited, errorMessage, activityId };
      }, [
        toolResult?.activityId,
        toolResult?.message,
        args?.filename,
        toolResult?.filePath,
        status,
      ]);

    const statusIcon = useMemo(() => getStatusIcon(status), [status]);

    const handleDiffClick = useCallback(() => {
      if (activityId) {
        openCodeDiffPanel(activityId);
        setTimeout(() => {
          setNewLayout(LeftTab.EngSpec);
        }, 50);
      }
    }, [activityId, openCodeDiffPanel, setNewLayout]);

    const containerClasses =
      "rounded-md text-xs my-2 bg-secondary-n-950 text-secondary-n-400 transition-colors duration-200 border border-[#2F3035]";

    const getAccordionTriggerClasses = () =>
      cn(
        "flex items-center text-xs font-medium text-secondary-n-400 px-0 py-1.5",
        "hover:no-underline",
        "[&[data-state=closed]>svg:last-child]:hidden [&[data-state=open]>svg:last-child]:hidden",
      );

    const getChevronClasses = () =>
      cn(
        "w-4 h-4 text-secondary-n-400 transition-all absolute inset-0",
        accordionValue === "tool-content"
          ? "rotate-90 opacity-100"
          : "opacity-0 group-hover/edit:opacity-100",
      );

    if (status === "succeeded" || status === "in-progress") {
      return (
        <div
          className={cn(
            containerClasses,
            "px-2 py-2 flex flex-col items-start justify-start group cursor-pointer",
            !hasDiffData && "cursor-default",
            hasDiffData && "hover:bg-secondary-n-900",
          )}
          onClick={() => {
            if (hasDiffData) {
              handleDiffClick();
            }
          }}
        >
          <div className="w-full grid grid-cols-[auto_minmax(0,1fr)_auto] gap-1 items-center">
            {statusIcon}
            <div className="flex items-center min-w-0">
              {fileEdited ? (
                <span className="text-xs truncate text-secondary-n-400">
                  {fileEdited}
                </span>
              ) : (
                <span className="text-xs text-secondary-n-500">
                  {getRandomStatusText()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <StoryCodeActionButtons
                filePath={fileEdited}
                className="shrink-0"
              />
            </div>
          </div>
        </div>
      );
    }

    // For failed/aborted status, show accordion with collapsible error
    return (
      <Accordion
        type="single"
        value={accordionValue}
        onValueChange={setAccordionValue}
        collapsible
        className={cn(containerClasses, "group/edit")}
      >
        <AccordionItem value="tool-content" className="border-none">
          <AccordionTrigger
            className={cn(getAccordionTriggerClasses(), "px-2 py-2")}
          >
            <div className="w-full grid grid-cols-[auto_minmax(0,1fr)] gap-2 items-center">
              <div className="relative w-4 h-4">
                <ChevronRightIcon className={getChevronClasses()} />
                <div
                  className={cn(
                    "absolute inset-0 transition-opacity",
                    accordionValue === "tool-content"
                      ? "opacity-0"
                      : "opacity-100 group-hover/edit:opacity-0",
                  )}
                >
                  {statusIcon}
                </div>
              </div>
              <div className="flex items-center min-w-0">
                <span className="text-xs truncate">{fileEdited}</span>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="transition-all relative ml-4 pb-0">
            <div className="absolute left-[-18px] top-0 bottom-0 w-px bg-secondary-n-700"></div>
            {/* Error message for failed/aborted status */}
            {errorMessage && (
              <div className="text-xs text-red-400 px-2 leading-relaxed">
                {errorMessage}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  });
